/* eslint-disable react/display-name */
import React from 'react'
import { Localized } from '@fluent/react'
import { useMoteDeleteMutation, useMoteStatusQuery } from '@mote/api'
import { useFlightRecorder } from '@mote/helpers/contexts'
import { Block, Button, Spinner, Text } from '@mote/ui'
import styled from '@xstyled/styled-components'
import {
  ControlsContainer,
  DeleteButton,
  Durations,
  PlayPauseButton,
  RewindButton,
  Scrubber
} from '../../../../shared/components'
import { useMotePlayer } from '../../../../shared/contexts/motePlayer.context'
import { Loader } from '../../shared/Loader'
import { Hint, textDefaultProps } from '../../shared/RecorderText'
import { DraggableRecorderLayout } from './DraggableRecorderLayout'

export const DraggableRecorderPlayback = React.forwardRef(
  ({ onInsert, onDelete, onClose, onError, isStandalone = true }, ref) => {
    const [mote, setMote] = React.useState(null)
    const [isDeleteMoteLoading, setIsDeleteMoteLoading] = React.useState(false)
    const cancelPollingTimeoutRef = React.useRef(null)
    const [loadingPollingLimit, setPollingPastLimit] = React.useState(false)
    const { flightState } = useFlightRecorder()

    const { mutate: checkStatus, data } = useMoteStatusQuery({
      moteId: flightState.moteId,
      refetchInterval: mote && mote.isPlayable ? false : 2000
    })

    const { mutate: deleteMutation } = useMoteDeleteMutation(flightState.moteId)

    const handleDelete = React.useCallback(async () => {
      setIsDeleteMoteLoading(true)
      await deleteMutation()
      onDelete?.()
      setIsDeleteMoteLoading(false)
    }, [onDelete])

    const options = React.useMemo(() => {
      return {
        init: true,
        src: mote?.playbackLocation ?? '',
        isStandalone,
        playbackCallbacks: { onDelete: handleDelete }
      }
    }, [mote, isStandalone, handleDelete])

    const { play, pause, playing, onRewind, getPlayerProps } =
      useMotePlayer(options)

    const playbackClose = async () => {
      await deleteMutation()
      onClose?.()
    }

    React.useEffect(() => {
      checkStatus()
    }, [])

    React.useEffect(() => {
      if (!data) return

      if (!data.moteStatus?.isPlayable) return

      setMote(data.moteStatus)
    }, [data])

    const isLoading = React.useMemo(() => !mote?.isPlayable, [mote])

    React.useEffect(() => {
      if (isLoading) {
        cancelPollingTimeoutRef.current = setTimeout(() => {
          setPollingPastLimit(true)

          cancelPollingTimeoutRef.current = setTimeout(() => {
            onError('POLLING_LIMIT_EXCEEDED')
          }, 25000)
        }, 15000)
        return
      }

      clearTimeout(cancelPollingTimeoutRef.current)
      cancelPollingTimeoutRef.current = null

      return () => clearTimeout(cancelPollingTimeoutRef.current)
    }, [isLoading])

    return (
      <DraggableRecorderLayout
        ref={ref}
        headerProps={{
          onClose: playbackClose,
          children: isLoading ? 'Just a second' : '👏 Done!'
        }}
        contentProps={getPlayerProps()}
      >
        {isLoading && (
          <BodyCentered align='center' justify='center' gap={32}>
            <SpinnerStyled
              radius={100}
              color='#AC0AE8'
              duration={1400}
              strokeWidth={3}
            />
            <Block gap={8}>
              <Text {...textDefaultProps}>
                {!loadingPollingLimit && (
                  <Localized id='rp--thinking'>Thinking...</Localized>
                )}
                {loadingPollingLimit && (
                  <Localized id='rp--thinking'>
                    This is taking a bit longer...
                  </Localized>
                )}
              </Text>
              <Localized id='you-can-move-window-around'>
                <Hint>(You can move this window around)</Hint>
              </Localized>
            </Block>
          </BodyCentered>
        )}
        {isDeleteMoteLoading && <Loader title='Deleting your mote...' />}
        {!(isLoading || isDeleteMoteLoading) && (
          <Body justify='space-between' gap={12} spreadY>
            <ControlsContainer gap={24}>
              <RewindButton
                size={42}
                disabled={!playing}
                aria-label='Rewind (press j)'
                data-testid='rewind-button'
                onClick={
                  playing
                    ? e => {
                        e.stopPropagation()
                        onRewind()
                      }
                    : null
                }
              />

              <PlayPauseButton
                size={70}
                data-testid={playing ? 'pause-button' : 'play-button'}
                aria-label={`${playing ? 'Pause' : 'Play'} (press k)`}
                isPlaying={playing}
                onClick={e => {
                  e.stopPropagation()
                  playing ? pause() : play()
                }}
              />

              <DeleteButton
                size={42}
                data-testid='delete-button'
                aria-label='Delete (press d)'
                onClick={handleDelete}
              />
            </ControlsContainer>

            <Block gap={4} spreadX>
              <Scrubber loading={isLoading} />
              <Durations />
            </Block>

            <Button text='Insert' onClick={() => onInsert(mote)} />

            <Localized id='you-can-move-window-around'>
              <Hint>(You can move this window around)</Hint>
            </Localized>
          </Body>
        )}
      </DraggableRecorderLayout>
    )
  }
)

const Body = styled(Block)`
  padding: 24px 40px;
`

const BodyCentered = styled(Block)`
  height: 250px;
  box-sizing: border-box;
`

const SpinnerStyled = styled(Spinner)``
